import { supabase } from "./client";
import { withRequestDeadline } from "./requestDeadline";

export const WAITER_HEARTBEAT_MS = 20000;
export const WAITER_PRESENCE_POLL_MS = 5000;

const presenceRpc = async (name, params) => {
  if (!supabase) throw new Error("No se pudo conectar con los mozos.");
  const { data, error } = await withRequestDeadline(
    (signal) => supabase.rpc(name, params).abortSignal(signal),
    { timeoutMs: 5000, message: "No se pudo verificar la conexion de los mozos." }
  );
  if (error) throw error;
  return data;
};

export const isWaiterConnected = (waiter, branchId, now = Date.now()) =>
  Boolean(
    waiter?.userId && waiter.branchId === branchId &&
    waiter.branchAccessActive === true && waiter.companyUserActive === true &&
    Number(waiter.connectedUntil) > now
  );

export const listConnectedBranchWaiters = async (branchId) => {
  if (!branchId) return [];
  const requestedAt = Date.now();
  const rows = await presenceRpc("list_connected_branch_waiters", { p_branch_id: branchId });
  // Use the server's remaining lease, not the device's timezone or a stored roster.
  return (Array.isArray(rows) ? rows : []).map((row) => ({
    userId: row.user_id,
    branchId: row.branch_id,
    profileFullName: row.full_name || "",
    branchAccessActive: true,
    companyUserActive: true,
    connectedUntil: requestedAt + Math.min(75000, Math.max(0, Number(row.lease_remaining_ms) || 0)),
  })).filter((waiter) => isWaiterConnected(waiter, branchId));
};

export const setMyWaiterPresence = (branchId, connectionId, online) =>
  presenceRpc("set_my_waiter_presence", {
    p_branch_id: branchId,
    p_connection_id: connectionId,
    p_online: online,
  });

export const startWaiterPresence = ({
  branchId,
  connectionId = window.crypto.randomUUID(),
  publish = setMyWaiterPresence,
  onError = () => {},
  onConnected = () => {},
}) => {
  let stopped = false;
  let pageAway = false;
  let dirty = false;
  let wantedOnline = false;
  let pending = null;

  const send = (online) => {
    wantedOnline = online;
    dirty = true;
    if (!pending) {
      // Serialize leave after any in-flight heartbeat so it cannot resurrect a closed session.
      pending = Promise.resolve().then(async () => {
        while (dirty) {
          dirty = false;
          const online = wantedOnline;
          try {
            await publish(branchId, connectionId, online);
            if (!stopped && online) onConnected();
          } catch (error) {
            if (!stopped) onError(error);
          }
        }
      }).finally(() => { pending = null; });
    }
    return pending;
  };
  const heartbeat = () => {
    if (!stopped && !pageAway && navigator.onLine !== false) void send(true);
  };
  const leave = () => { pageAway = true; void send(false); };
  const resume = () => { pageAway = false; heartbeat(); };
  const visible = () => { if (document.visibilityState === "visible") heartbeat(); };
  const offline = () => { void send(false); };
  window.addEventListener("online", heartbeat);
  window.addEventListener("offline", offline);
  window.addEventListener("pagehide", leave);
  window.addEventListener("pageshow", resume);
  document.addEventListener("visibilitychange", visible);
  const timer = window.setInterval(heartbeat, WAITER_HEARTBEAT_MS);
  heartbeat();

  return {
    stop: () => {
      if (stopped) return pending || Promise.resolve();
      stopped = true;
      window.clearInterval(timer);
      window.removeEventListener("online", heartbeat);
      window.removeEventListener("offline", offline);
      window.removeEventListener("pagehide", leave);
      window.removeEventListener("pageshow", resume);
      document.removeEventListener("visibilitychange", visible);
      return send(false);
    },
  };
};
